import { useState, useEffect, useMemo } from 'react';
import { usersApi, accountsApi } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { Spinner, Alert, Pagination } from '@rankhelm/ui';
import type { User, Account, UserInvitation } from '@rankhelm/shared';

const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin',
  account_admin: 'Account Admin',
  manager: 'Manager',
  editor: 'Editor',
  viewer: 'Viewer',
};

const ROLE_COLORS: Record<string, string> = {
  super_admin: 'bg-purple-500/20 text-purple-400',
  account_admin: 'bg-helm-500/20 text-helm-400',
  manager: 'bg-amber-500/20 text-amber-400',
  editor: 'bg-blue-500/20 text-blue-400',
  viewer: 'bg-green-500/20 text-green-400',
};

const ROLE_ORDER = ['super_admin', 'account_admin', 'manager', 'editor', 'viewer'];
const PAGE_SIZE = 20;

export function TeamManagementPage() {
  const { user: currentUser, account } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [invitations, setInvitations] = useState<UserInvitation[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Filters
  const [filterRole, setFilterRole] = useState('all');
  const [filterOrgId, setFilterOrgId] = useState('all');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Invite form
  const [showInvite, setShowInvite] = useState(false);
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('editor');
  const [inviteOrgId, setInviteOrgId] = useState('');
  const [inviting, setInviting] = useState(false);

  const isSuperAdmin = currentUser?.role === 'super_admin';
  const isOrgAdmin = currentUser?.role === 'account_admin';
  const isManager = currentUser?.role === 'manager';
  // Team Management is visible to managers (view-only) and above.
  // Viewers and editors should not see this page.
  const canView = isSuperAdmin || isOrgAdmin || isManager;
  const canManage = isSuperAdmin || isOrgAdmin;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const usersPromise = usersApi.list();
      const invPromise = usersApi.listInvitations().catch(() => ({ data: [] as UserInvitation[] }));
      const acctPromise = currentUser?.role === 'super_admin'
        ? accountsApi.list().catch(() => ({ data: [] as Account[] }))
        : null;

      const [usersRes, invRes, acctRes] = await Promise.all([
        usersPromise,
        invPromise,
        acctPromise,
      ]);

      setUsers((usersRes.data as User[]) || []);
      setInvitations(invRes.data || []);
      if (acctRes) {
        const accountData = acctRes.data;
        setAccounts(Array.isArray(accountData) ? accountData as Account[] : accountData ? [accountData as Account] : []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  // Build org lookup map
  const accountMap = useMemo(() => {
    const map: Record<string, string> = {};
    accounts.forEach((a) => { map[a.id] = a.name; });
    return map;
  }, [accounts]);

  // Filtered & sorted users
  const filteredUsers = useMemo(() => {
    let result = [...users];
    if (filterRole !== 'all') {
      result = result.filter((u) => u.role === filterRole);
    }
    if (filterOrgId !== 'all') {
      if (filterOrgId === 'none') {
        result = result.filter((u) => !u.account_id);
      } else {
        result = result.filter((u) => u.account_id === filterOrgId);
      }
    }
    result.sort((a, b) => ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role));
    return result;
  }, [users, filterRole, filterOrgId]);

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / PAGE_SIZE);
  const paginatedUsers = filteredUsers.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterRole, filterOrgId]);

  const handleInvite = async () => {
    if (!inviteEmail.trim() || !inviteFirstName.trim() || !inviteLastName.trim()) return;
    if (isSuperAdmin && !inviteOrgId) {
      setError('Please select an account for this user.');
      return;
    }
    setInviting(true);
    setError('');
    setSuccess('');
    try {
      const payload: { email: string; first_name: string; last_name: string; role: string; account_id?: string } = {
        email: inviteEmail,
        first_name: inviteFirstName,
        last_name: inviteLastName,
        role: inviteRole,
      };
      if (isSuperAdmin && inviteOrgId) {
        payload.account_id = inviteOrgId;
      }
      await usersApi.invite(payload);
      setSuccess(`Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      setInviteFirstName('');
      setInviteLastName('');
      setInviteOrgId('');
      setShowInvite(false);
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    setError('');
    try {
      await usersApi.updateRole(userId, newRole as import('@rankhelm/shared').UserRole);
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
    }
  };

  const handleRevokeInvitation = async (id: string) => {
    try {
      await usersApi.revokeInvitation(id);
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke invitation');
    }
  };

  const copyInviteLink = async (token: string, invId: string) => {
    const url = `${window.location.origin}/accept-invite?token=${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(invId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedId(invId);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  if (!canView) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-slate-400">
          You don't have permission to access Team Management.
        </div>
      </div>
    );
  }

  if (loading) {
    return <Spinner message="Loading team..." fullPage />;
  }

  const pendingInvites = invitations.filter((i) => !i.accepted_at);

  // Super Admin is not an assignable/visible role for agency admins.
  // Only super admins themselves see the Super Admin entry in dropdowns.
  const visibleRoleOrder = isSuperAdmin
    ? ROLE_ORDER
    : ROLE_ORDER.filter((r) => r !== 'super_admin');

  // Available roles for invite — org admins can create managers/editors/viewers
  const availableInviteRoles = isSuperAdmin
    ? [
        { value: 'account_admin', label: 'Account Admin' },
        { value: 'manager', label: 'Manager' },
        { value: 'editor', label: 'Editor' },
        { value: 'viewer', label: 'Viewer (Client)' },
      ]
    : [
        { value: 'manager', label: 'Manager' },
        { value: 'editor', label: 'Editor' },
        { value: 'viewer', label: 'Viewer (Client)' },
      ];

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl font-bold text-white">Team Management</h1>
        {canManage && (
          <button
            onClick={() => setShowInvite(!showInvite)}
            className="px-4 py-2.5 bg-helm-500 hover:bg-helm-400 text-white text-sm font-medium rounded-lg transition-colors"
          >
            + Invite User
          </button>
        )}
      </div>
      <p className="text-sm text-slate-500 mb-6">
        {isSuperAdmin ? 'Manage all users across accounts' : account?.name || 'Your account'}
      </p>

      {error && <Alert variant="error">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      {/* Invite Form */}
      {showInvite && (
        <div className="bg-navy-900 border border-navy-700 rounded-xl p-6 mb-6">
          <h3 className="text-white font-medium mb-2">Invite Team Member</h3>
          <p className="text-xs text-slate-500 mb-4">
            {isSuperAdmin
              ? 'Assign the user to an account. Admins manage their own managers, editors, and viewers.'
              : 'Managers oversee content. Editors create content. Viewers are read-only clients.'}
          </p>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                type="text"
                value={inviteFirstName}
                onChange={(e) => setInviteFirstName(e.target.value)}
                placeholder="First name"
                className="px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500"
              />
              <input
                type="text"
                value={inviteLastName}
                onChange={(e) => setInviteLastName(e.target.value)}
                placeholder="Last name"
                className="px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500"
              />
            </div>
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="Email address"
              className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500"
            />
            <div className="flex flex-col sm:flex-row gap-3">
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-helm-500"
              >
                {availableInviteRoles.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
              {isSuperAdmin && (
                <select
                  value={inviteOrgId}
                  onChange={(e) => setInviteOrgId(e.target.value)}
                  className={`flex-1 px-4 py-2.5 bg-navy-800 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-helm-500 ${
                    !inviteOrgId ? 'border-amber-500/50' : 'border-navy-600'
                  }`}
                >
                  <option value="">— Select Account —</option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              )}
              <button
                onClick={handleInvite}
                disabled={inviting || !inviteFirstName.trim() || !inviteLastName.trim() || !inviteEmail.trim() || (isSuperAdmin && !inviteOrgId)}
                className="px-6 py-2.5 bg-helm-500 hover:bg-helm-400 text-white font-medium rounded-lg transition-colors disabled:opacity-50 whitespace-nowrap"
              >
                {inviting ? 'Sending...' : 'Send Invite'}
              </button>
            </div>
          </div>
          {isSuperAdmin && !inviteOrgId && (
            <p className="text-xs text-amber-400 mt-2">Account is required when inviting a user.</p>
          )}
          <p className="text-xs text-slate-500 mt-2">
            Copy the invite link from the pending invitations below and share it with the user.
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          className="px-3 py-2 bg-navy-900 border border-navy-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-helm-500"
        >
          <option value="all">All Roles</option>
          {visibleRoleOrder.map((r) => (
            <option key={r} value={r}>{ROLE_LABELS[r]}</option>
          ))}
        </select>
        {isSuperAdmin && (
          <select
            value={filterOrgId}
            onChange={(e) => setFilterOrgId(e.target.value)}
            className="px-3 py-2 bg-navy-900 border border-navy-700 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-helm-500"
          >
            <option value="all">All Accounts</option>
            <option value="none">No Account</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        )}
        <span className="text-xs text-slate-500 self-center">
          {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Users Table */}
      <div className="bg-navy-900 border border-navy-700 rounded-xl overflow-hidden mb-6">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="border-b border-navy-700">
                <th className="text-left text-xs font-medium text-slate-400 px-5 py-3">Name</th>
                <th className="text-left text-xs font-medium text-slate-400 px-5 py-3">Email</th>
                <th className="text-left text-xs font-medium text-slate-400 px-5 py-3">Role</th>
                {isSuperAdmin && (
                  <th className="text-left text-xs font-medium text-slate-400 px-5 py-3">Account</th>
                )}
                <th className="text-left text-xs font-medium text-slate-400 px-5 py-3 hidden md:table-cell">Joined</th>
                {canManage && (
                  <th className="text-left text-xs font-medium text-slate-400 px-5 py-3">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-slate-500 text-sm">
                    No users match the current filters.
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((u) => (
                  <tr key={u.id} className="border-b border-navy-700/50 hover:bg-navy-800/50 transition-colors">
                    <td className="px-5 py-3">
                      <span className="text-white text-sm font-medium">{u.name}</span>
                      {u.id === currentUser?.id && (
                        <span className="ml-2 text-xs text-slate-500">(you)</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-slate-400 text-sm">{u.email}</td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${ROLE_COLORS[u.role] || ''}`}>
                        {ROLE_LABELS[u.role] || u.role}
                      </span>
                    </td>
                    {isSuperAdmin && (
                      <td className="px-5 py-3 text-slate-400 text-sm">
                        {u.account_id ? (accountMap[u.account_id] || u.account_id.slice(0, 8) + '...') : (
                          <span className="text-slate-600 italic">None</span>
                        )}
                      </td>
                    )}
                    <td className="px-5 py-3 text-slate-500 text-xs hidden md:table-cell">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : '--'}
                    </td>
                    {canManage && (
                      <td className="px-5 py-3">
                        {u.id !== currentUser?.id && u.role !== 'super_admin' && (
                          <select
                            value={u.role}
                            onChange={(e) => handleRoleChange(u.id, e.target.value)}
                            className="text-xs px-2 py-1 bg-navy-800 border border-navy-600 rounded text-slate-300 focus:outline-none"
                          >
                            {isOrgAdmin && <option value="account_admin">Account Admin</option>}
                            {isSuperAdmin && <option value="account_admin">Account Admin</option>}
                            <option value="manager">Manager</option>
                            <option value="editor">Editor</option>
                            <option value="viewer">Viewer</option>
                          </select>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-5 py-3 border-t border-navy-700">
            <Pagination
              currentPage={currentPage}
              totalItems={filteredUsers.length}
              pageSize={PAGE_SIZE}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>

      {/* Pending Invitations */}
      {pendingInvites.length > 0 && (
        <div className="bg-navy-900 border border-navy-700 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-navy-700">
            <h3 className="text-sm font-medium text-slate-300">{pendingInvites.length} Pending Invitation{pendingInvites.length !== 1 ? 's' : ''}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="border-b border-navy-700">
                  <th className="text-left text-xs font-medium text-slate-400 px-5 py-3">Name</th>
                  <th className="text-left text-xs font-medium text-slate-400 px-5 py-3">Email</th>
                  <th className="text-left text-xs font-medium text-slate-400 px-5 py-3">Role</th>
                  {isSuperAdmin && (
                    <th className="text-left text-xs font-medium text-slate-400 px-5 py-3">Account</th>
                  )}
                  <th className="text-left text-xs font-medium text-slate-400 px-5 py-3">Invite Link</th>
                  <th className="text-left text-xs font-medium text-slate-400 px-5 py-3 hidden md:table-cell">Expires</th>
                  <th className="text-left text-xs font-medium text-slate-400 px-5 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingInvites.map((inv) => (
                  <tr key={inv.id} className="border-b border-navy-700/50">
                    <td className="px-5 py-3 text-white text-sm">
                      {inv.first_name && inv.last_name
                        ? `${inv.first_name} ${inv.last_name}`
                        : <span className="text-slate-500 italic">--</span>
                      }
                    </td>
                    <td className="px-5 py-3 text-slate-400 text-sm">{inv.email}</td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${ROLE_COLORS[inv.role] || ''}`}>
                        {ROLE_LABELS[inv.role] || inv.role}
                      </span>
                    </td>
                    {isSuperAdmin && (
                      <td className="px-5 py-3 text-slate-400 text-sm">
                        {inv.account_id ? (accountMap[inv.account_id] || inv.account_id.slice(0, 8) + '...') : (
                          <span className="text-slate-600 italic">None</span>
                        )}
                      </td>
                    )}
                    <td className="px-5 py-3">
                      <button
                        onClick={() => copyInviteLink(inv.token, inv.id)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          copiedId === inv.id
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-helm-500/15 text-helm-400 hover:bg-helm-500/25'
                        }`}
                      >
                        {copiedId === inv.id ? (
                          <>
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                            Copied!
                          </>
                        ) : (
                          <>
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                            Copy link
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-5 py-3 text-slate-500 text-xs hidden md:table-cell">{new Date(inv.expires_at).toLocaleDateString()}</td>
                    <td className="px-5 py-3">
                      <button
                        onClick={() => handleRevokeInvitation(inv.id)}
                        className="text-xs text-red-400 hover:text-red-300 transition-colors"
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
